import { useId, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { Mail, Lock, Eye, EyeOff, Info, MessageCircle, Phone } from 'lucide-react';
import type { TFunction } from 'i18next';
import { Input, Button, Checkbox, Label, Popover, PopoverContent, PopoverTrigger } from '@shared/ui';
import { PasswordPolicyChecklist, type PasswordPolicyRule } from '@entities/auth';

export interface RegistrationFormData {
  email: string;
  password: string;
  confirmPassword: string;
  telegram: string;
  whatsapp: string;
  termsAccepted: boolean;
  usResidencyConfirmed: boolean;
  marketingAccepted: boolean;
}

export type RegistrationBlurField = 'email' | 'password' | 'confirmPassword' | 'telegram' | 'whatsapp';

interface RegistrationFormProps {
  t: TFunction<'auth'>;
  formData: RegistrationFormData;
  setFormData: Dispatch<SetStateAction<RegistrationFormData>>;
  errors: Record<string, string>;
  policyRules: PasswordPolicyRule[];
  setPolicyRules: Dispatch<SetStateAction<PasswordPolicyRule[]>>;
  showPassword: boolean;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
  showConfirmPassword: boolean;
  setShowConfirmPassword: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  onSubmit: (e: FormEvent) => void;
  /** Проверка поля при уходе из него. */
  onFieldBlur?: (field: RegistrationBlurField) => void;
  onSwitchToLogin?: () => void;
}

export function RegistrationForm({
  t,
  formData,
  setFormData,
  errors,
  policyRules,
  setPolicyRules,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  isLoading,
  onSubmit,
  onFieldBlur,
  onSwitchToLogin,
}: RegistrationFormProps) {
  const fieldId = useId();
  const termsId = `${fieldId}-terms`;
  const usResidencyId = `${fieldId}-us-residency`;
  const marketingId = `${fieldId}-marketing`;
  const fieldError = (name: keyof typeof errors) => (errors[name] ? t(errors[name]) : undefined);

  return (
    <>
      <form onSubmit={onSubmit} className="flex w-full flex-col items-center gap-6">
        <div className="flex w-full flex-col gap-4 self-stretch">
          <Input
            type="email"
            placeholder={t('email')}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            onBlur={() => onFieldBlur?.('email')}
            icon={<Mail className="h-4 w-4" />}
            error={fieldError('email')}
            disabled={isLoading}
          />

          <div className="relative w-full">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder={t('password')}
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                setPolicyRules([]);
              }}
              onBlur={() => onFieldBlur?.('password')}
              icon={<Lock className="h-4 w-4" />}
              error={errors.password === 'recovery.policyLabel' ? undefined : fieldError('password')}
              invalid={Boolean(errors.password)}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-2.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <PasswordPolicyChecklist password={formData.password} failedRules={policyRules} />
          </div>

          <div className="relative w-full">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t('confirmPassword')}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            onBlur={() => onFieldBlur?.('confirmPassword')}
              icon={<Lock className="h-4 w-4" />}
              error={fieldError('confirmPassword')}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-2.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <Input
            type="text"
            name="telegram"
            autoComplete="off"
            placeholder={t('telegram')}
            value={formData.telegram}
            onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
            onBlur={() => onFieldBlur?.('telegram')}
            icon={<MessageCircle className="h-4 w-4" />}
            error={fieldError('telegram')}
            disabled={isLoading}
          />

          <Input
            type="tel"
            name="whatsapp"
            autoComplete="tel"
            inputMode="numeric"
            placeholder={t('whatsapp')}
            value={formData.whatsapp}
            onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
            onBlur={() => onFieldBlur?.('whatsapp')}
            icon={<Phone className="h-4 w-4" />}
            error={fieldError('whatsapp')}
            disabled={isLoading}
          />

          <div className="flex items-start gap-2">
            <Checkbox
              id={termsId}
              checked={formData.termsAccepted}
              onCheckedChange={(checked) => setFormData({ ...formData, termsAccepted: checked as boolean })}
            />
            <Label htmlFor={termsId} className="cursor-pointer text-xs leading-4 text-foreground">
              {t('agreeToTerms')}
            </Label>
          </div>
          {errors.termsAccepted && <p className="text-xs text-destructive -mt-2 ml-6">{fieldError('termsAccepted')}</p>}

          <div className="flex items-start gap-2">
            <Checkbox
              id={usResidencyId}
              checked={formData.usResidencyConfirmed}
              onCheckedChange={(checked) => setFormData({ ...formData, usResidencyConfirmed: checked as boolean })}
            />
            <Label htmlFor={usResidencyId} className="cursor-pointer text-xs leading-4 text-foreground">
              {t('agreeToUsResidency')}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={t('usResidencyTooltip')}
                  className="-m-2 shrink-0 p-2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Info className="h-4 w-4" aria-hidden />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" collisionPadding={8} className="max-w-[calc(100vw-2rem)] text-xs leading-4">
                {t('usResidencyTooltip')}
              </PopoverContent>
            </Popover>
          </div>
          {errors.usResidencyConfirmed && (
            <p className="text-xs text-destructive -mt-2 ml-6">{fieldError('usResidencyConfirmed')}</p>
          )}

          <div className="flex items-start gap-2">
            <Checkbox
              id={marketingId}
              checked={formData.marketingAccepted}
              onCheckedChange={(checked) => setFormData({ ...formData, marketingAccepted: checked as boolean })}
            />
            <Label htmlFor={marketingId} className="cursor-pointer text-xs leading-4 text-foreground">
              {t('agreeToMarketing')}
            </Label>
          </div>
        </div>

        <Button type="submit" variant="default" className="w-full" loading={isLoading}>
          {t('createAccount')}
        </Button>
      </form>

      <div className="flex w-full flex-row flex-wrap items-center justify-center gap-1 text-center">
        <span className="text-sm leading-5 text-muted-foreground">{t('alreadyHaveAccount')}</span>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-sm leading-5 text-foreground underline transition-colors hover:text-primary"
        >
          {t('signIn')}
        </button>
      </div>
    </>
  );
}
